import { QuoteGetResponse } from '@lio/api-types';
import { QuoteType } from '@models/quotes';
import { QuoteLocations } from '@models/customer';
import { Box, Grid, Typography } from '@mui/material';
import { formatDateToTimezone } from '@utils/date';
import { USDollar } from '@utils/number';
import React, { useMemo } from 'react';
import { formatAddress } from '@utils/address';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import { QuoteBindingStatus } from '@lio/rule-engine';
import SwimClubLogo from '@assets/image/SwimClubLogo.png';
import { useSearchQuoteRatings, searchLastSuccessfulRatingParams } from '@services/quoteRating';
import { writingCompanyIds } from '@models/productOffering';

export interface SidePanelProps {
  quote: QuoteGetResponse;
  type: PortalMessageType;
  premium?: number;
  policyNumber: string;
  multiPolicyQuoteBindings?: {
    policyNumber: string;
    termPremium: number;
    termTaxFeeSurcharge: number;
    status?: QuoteBindingStatus;
  }[];
}

export const SidePanel = ({ quote, multiPolicyQuoteBindings, type, premium, policyNumber }: SidePanelProps) => {
  const locations = quote?.quoteLocations || [];

  const { data: lastSuccessfulRating } = useSearchQuoteRatings({
    quoteId: quote.id,
    enabled: type === PortalMessageType.Bind || type === PortalMessageType.Bound,
    params: searchLastSuccessfulRatingParams,
  });

  const latestRating = lastSuccessfulRating?.pages[0].items[0];

  const isSpecialty = quote?.writingCompany?.id === writingCompanyIds.specialty;

  const data: { title: string; body?: any; listBody?: string[] }[] = useMemo(() => {
    const result = [];
    if (type === PortalMessageType.Bind || type === PortalMessageType.Bound) {
      if (multiPolicyQuoteBindings) {
        result.push({
          title: 'Policies:',
          listBody: multiPolicyQuoteBindings.flatMap((quote) => `#${quote.policyNumber}`),
        });
      } else if (quote.policyNumber && quote.offering.insuranceProduct && quote.writingCompany) {
        result.push({
          title: 'Policy #:',
          body: policyNumber,
        });
      }
      if (quote?.effectiveOn) {
        result.push({
          title: quote.quoteType === QuoteType.Endorsement ? 'Endorsement Effective Date:' : 'Policy Effective Date:',
          body: formatDateToTimezone(quote?.effectiveOn),
        });
      }
      if (quote.quoteType === QuoteType.Endorsement) {
        result.push(
          {
            title: 'New Policy Premium:',
            body: `${USDollar.format(latestRating?.endorsementDetails?.newPolicyCost || 0)}`,
          },
          {
            title: 'Endorsement Premium Change:',
            body: `${USDollar.format(latestRating?.endorsementDetails?.endorsementPremiumChange || 0)}`,
          },
        );
      }
    } else {
      if (quote?.offering.label || quote?.offering.insuranceProduct.name) {
        result.push({
          title: 'Product: ',
          body: quote?.offering.label ?? quote?.offering.insuranceProduct.name,
        });
      }
      if (quote?.productLabel) {
        result.push({
          title: 'Policy Option',
          body: quote?.productLabel.name,
        });
      }
      if (
        quote?.productOption?.name &&
        ![PortalMessageType.CustomerSubmission, PortalMessageType.CustomerUserBlocking].includes(type)
      ) {
        result.push({
          title: 'Business Class: ',
          body: quote?.productOption?.name,
        });
      }
      if (quote?.effectiveOn) {
        result.push({
          title: 'Effective Date: ',
          body: formatDateToTimezone(quote?.effectiveOn),
        });
      }
    }

    if (premium && quote.quoteType !== QuoteType.Endorsement) {
      const getPremiumTitle = () => {
        if (type === PortalMessageType.Referred) {
          return 'Estimated Premium';
        }
        return 'Premium';
      };

      if (multiPolicyQuoteBindings) {
        // if values are not equal lengths we have not received all the correct premiums
        const showPremiums =
          multiPolicyQuoteBindings?.length ===
          multiPolicyQuoteBindings?.filter((x) => x.termPremium !== null && x.termPremium !== 0)?.length;
        if (!showPremiums) {
          result.push({
            title: `${getPremiumTitle()}s:`,
            listBody: multiPolicyQuoteBindings.map(
              (quote) => `${USDollar.format(quote.termPremium + quote.termTaxFeeSurcharge)}`,
            ),
          });
        }
      } else {
        if (isSpecialty && type === PortalMessageType.Referred) {
          return result;
        }
        result.push({
          title: `${getPremiumTitle()}:`,
          body: `${USDollar.format(premium)}`,
        });
      }
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, quote, multiPolicyQuoteBindings, premium, policyNumber]);
  return (
    <Grid
      sx={{
        width: { xs: '100%', sm: '400px' },
        padding: { xs: '16px 8px', sm: '48px' },
        backgroundColor: 'rgba(255, 255, 255, 0.1)', // TODO: use theme
        boxShadow: 'boxShadow.3',
        borderRadius: (theme) => theme.shape.borderRadius * 2,
      }}
    >
      {type === PortalMessageType.CustomerSubmission && (
        <Box
          sx={{
            marginBottom: '28px',
            paddingBottom: '28px',
            borderBottom: '1px solid gray',
            borderColor: 'other.divider',
          }}
          component='img'
          src={SwimClubLogo}
          alt='Zillow'
          width='308px'
          height='auto'
        />
      )}
      <Typography
        variant='bodySmall'
        sx={{
          display: 'block',
        }}
      >
        Named Insured:
      </Typography>
      <Typography
        variant='bodySmall'
        data-testid='policyNamedInsured'
        sx={{
          display: 'block',
          fontWeight: 'bold',
        }}
      >
        {quote.customer.name}
      </Typography>
      {locations?.length > 0 ? (
        <>
          <Typography
            variant='bodySmall'
            sx={{
              display: 'block',
              marginTop: '20px',
            }}
          >
            Insured Address(es):
          </Typography>
          {locations.map((location: QuoteLocations) => {
            const { address } = location.insuredLocation;
            const fullAddress = formatAddress(address);
            return (
              <Typography
                data-testid='policyAddress'
                variant='bodySmall'
                key={fullAddress}
                sx={{
                  display: 'block',
                  fontWeight: 'bold',
                }}
              >
                {fullAddress}
              </Typography>
            );
          })}
        </>
      ) : null}
      {data?.map((el) => (
        <React.Fragment key={el.title}>
          <Typography
            variant='bodySmall'
            sx={{
              display: 'block',
              marginTop: '20px',
            }}
          >
            {el.title}
          </Typography>

          {el.listBody &&
            el.listBody.map((item) => (
              <Typography key={item} variant='bodySmall' display='block' fontWeight='bold' data-testid='policyNumber'>
                {item}
              </Typography>
            ))}

          {el.body && (
            <Typography
              variant='bodySmall'
              data-testid='policyNumber'
              sx={{
                display: 'block',
                fontWeight: 'bold',
              }}
            >
              {el.body}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Grid>
  );
};
